export type PollPromiseCancel = () => void;

/**
 * Calls `promise` up to `times`, waiting `interval` ms between attempts.
 * Invokes `onComplete` after the final attempt settles (success or failure).
 * Returns a cancel function that stops further polling.
 */
export const pollPromise = (
  promise: () => Promise<unknown>,
  onComplete: () => void,
  times: number = 5,
  interval: number = 1000
): PollPromiseCancel => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let stopProcessing = false;

  const cancel: PollPromiseCancel = () => {
    stopProcessing = true;
    if (timeoutId) clearTimeout(timeoutId);
  };

  const call = (remaining: number) => {
    let attempt: Promise<unknown>;
    try {
      attempt = Promise.resolve(promise());
    } catch {
      // Sync throws should not stop polling.
      attempt = Promise.resolve();
    }

    // Schedule the next attempt in finally so rejections/errors still continue polling.
    // Catch after finally to avoid unhandled rejection noise from failed attempts.
    attempt
      .finally(() => {
        // If cancel is called, we don't want to poll again but we
        // also don't want to execute anything within this resolver.
        if (stopProcessing) return;

        if (remaining > 1) {
          timeoutId = setTimeout(call, interval, remaining - 1);
        } else {
          onComplete();
        }
      })
      .catch(() => {});
  };

  call(times);

  return cancel;
};
